import { BASE_AUTH_OFFICE_API_V2 } from '@/core/api-config';
import { TokenManager } from "@/core/auth/token-manager";
import { ActorHierarchyItem } from '../types/actor-types';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import { useOperatorStore } from '@/core/stores/operator-store';
import {
  ConnectorDashboardRequest,
  ConnectorDashboardResponse,
  ConnectorCountResponse,
} from '../types/api-types';


export class ActorApi {
    constructor(
        private readonly baseUrl: string = BASE_AUTH_OFFICE_API_V2,
        private readonly tokenManager: TokenManager
    ) { }

    private async getAuthHeaders(): Promise<Record<string, string>> {
        const operatorId = useOperatorStore.getState().selectedOperator?.operatorId;
        if (!operatorId) {
            throw new Error('No operator selected.');
        }
        const headers = await this.tokenManager.getAuthHeaders();
        return {
            ...headers,
            'Content-Type': 'application/json',
            'X-Operator-Id': operatorId.toString(),
        };
    }

    // V2: GET /api/v2/actor/{operatorId}/hierarchy
    async getActorHierarchy(operatorId: string): Promise<ActorHierarchyItem[]> {
        const headers = await this.getAuthHeaders();
        const url = `${this.baseUrl}/actor/${operatorId}/hierarchy?includeChildren=true`;

        const response = await ApiErrorMiddleware.fetchWithErrorHandling(
            url,
            { method: 'GET', headers },
            'Actor API',
            `/actor/${operatorId}/hierarchy`
        );

        const data = await response.json();

        if (!data.isSuccess) {
            throw new Error(data.errorMessage || 'Failed to load actor hierarchy');
        }

        return (data.actors ?? []) as ActorHierarchyItem[];
    }

    async getAllMachinesForOperator(skip: number, take: number): Promise<{ rows: any[]; count?: number }> {
        const selectedOperator = useOperatorStore.getState().selectedOperator;
        if (!selectedOperator?.operatorId) {
            throw new Error('No operator selected. Cannot fetch machine list.');
        }

        const headers = await this.tokenManager.getAuthHeaders();
        const headersWithOperator = {
            ...headers,
            'X-Operator-Id': selectedOperator.operatorId.toString(),
        };

        const url = `${this.baseUrl}/actor/machines?$top=${take}&$skip=${skip}`;

        const response = await ApiErrorMiddleware.fetchWithErrorHandling(
            url,
            { method: 'GET', headers: { 'Content-Type': 'application/json', ...headersWithOperator } },
            'Actor API',
            '/actor/machines'
        );
        return response.json();
    }

    // V2: GET /api/v2/actor/{operatorId}/connectors
    async getConnectors(
        operatorId: string,
        params?: ConnectorDashboardRequest
    ): Promise<ConnectorDashboardResponse> {
        const headers = await this.getAuthHeaders();

        const query = new URLSearchParams();

        if (params?.actorIds?.length) {
            query.set('actorIds', params.actorIds.join(','));
        }
        if (params?.chargerStatuses?.length) {
            params.chargerStatuses.forEach(s => query.append('chargerStatusName', s));
        }
        if (params?.excludeInactive) {
            query.set('excludeInactive', 'true');
        }
        if (params?.ocppConnected !== undefined) {
            query.set('ocppConnected', params.ocppConnected.toString());
        }
        if (params?.hasActiveSession !== undefined) {
            query.set('hasActiveSession', params.hasActiveSession.toString());
        }
        if (params?.minUtilization !== undefined) {
            query.set('minUtilization', params.minUtilization.toString());
        }
        if (params?.maxUtilization !== undefined) {
            query.set('maxUtilization', params.maxUtilization.toString());
        }
        if (params?.limit !== undefined) {
            query.set('limit', params.limit.toString());
        }
        if (params?.offset !== undefined) {
            query.set('offset', params.offset.toString());
        }
        if (params?.sortBy) {
            query.set('sortBy', params.sortBy);
        }
        if (params?.sortOrder) {
            query.set('sortOrder', params.sortOrder);
        }

        const url = `${this.baseUrl}/actor/${operatorId}/connectors?${query}`;

        const response = await ApiErrorMiddleware.fetchWithErrorHandling(
            url,
            { method: 'GET', headers },
            'Actor API',
            `/actor/${operatorId}/connectors`
        );

        return response.json();
    }

    // V2: GET /api/v2/actor/{operatorId}/connectors/count
    async getConnectorCount(
        operatorId: string,
        params?: Omit<ConnectorDashboardRequest, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>
    ): Promise<ConnectorCountResponse> {
        const headers = await this.getAuthHeaders();

        const query = new URLSearchParams();

        if (params?.actorIds?.length) {
            query.set('actorIds', params.actorIds.join(','));
        }
        if (params?.chargerStatuses?.length) {
            params.chargerStatuses.forEach(s => query.append('chargerStatusName', s));
        }
        if (params?.excludeInactive) {
            query.set('excludeInactive', 'true');
        }
        if (params?.ocppConnected !== undefined) {
            query.set('ocppConnected', params.ocppConnected.toString());
        }
        if (params?.hasActiveSession !== undefined) {
            query.set('hasActiveSession', params.hasActiveSession.toString());
        }
        if (params?.minUtilization !== undefined) {
            query.set('minUtilization', params.minUtilization.toString());
        }
        if (params?.maxUtilization !== undefined) {
            query.set('maxUtilization', params.maxUtilization.toString());
        }

        const url = `${this.baseUrl}/actor/${operatorId}/connectors/count?${query}`;

        const response = await ApiErrorMiddleware.fetchWithErrorHandling(
            url,
            { method: 'GET', headers },
            'Actor API',
            `/actor/${operatorId}/connectors/count`
        );

        return response.json();
    }
}
